import { nanoid } from '@reduxjs/toolkit';
import { store } from '@shared/redux';
import ReconnectingWebSocket from 'reconnecting-websocket';
import { TinyEmitter } from 'tiny-emitter';
import { EVENT_MESSAGES, type EventName, type EventPayloadByName } from './types';
import { refreshToken } from '@shared/utils/refresh-token';
import { waitRehydration } from '@shared/utils/wait-rehydration';
import { isLocalDevRuntime } from './local-dev';

enum InternalEvents {
  OPEN = '@internal/open',
  CLOSE = '@internal/close',
  CALLBACK = '@internal/callback',
}

const PONG_TIMEOUT_MS = 5_000;
const HEARTBEAT_INTERVAL_MS = 15_000;
const MIN_RECONNECTION_DELAY_MS = 1_000;
const MAX_RECONNECTION_DELAY_MS = 30_000;
const RECONNECTION_GROW_FACTOR = 1.7;
const CONNECTION_TIMEOUT_MS = 5_000;
const MIN_UPTIME_MS = 5_000;

export class SocketClient {
  #url: string;
  #socket: ReconnectingWebSocket | null = null;
  #eventBus = new TinyEmitter();
  #messageQueue: string[] = [];
  #shouldReconnect = true;

  #heartbeatIntervalId: number | undefined;
  #pongTimeoutId: number | undefined;

  #unsubscribeStore?: () => void;
  #currentAccessToken?: string;
  #lastAuthorizedToken?: string;
  #failedAuthToken?: string;
  #hasFailedAuth = false;

  #wsAuthorized = false;
  #authRefreshPromise: Promise<boolean> | null = null;
  #connectPromise: Promise<void> | null = null;

  #failedAuthTimestamps: number[] = [];
  #FAILED_AUTH_WINDOW_MS = 10_000;
  #FAILED_AUTH_MAX = 3;

  constructor(url: string) {
    this.#url = url;
    this.#ensureStoreSubscription();
  }

  #ensureStoreSubscription() {
    if (this.#unsubscribeStore) return;

    this.#currentAccessToken = store.getState().auth.authInfo?.access_token;

    this.#unsubscribeStore = store.subscribe(() => {
      const token = store.getState().auth.authInfo?.access_token;
      if (token !== this.#currentAccessToken) {
        this.#currentAccessToken = token;

        if (token && this.#socket?.readyState === WebSocket.OPEN) {
          this.#authorize();
        }

        if (token && this.#hasFailedAuth && token !== this.#failedAuthToken) {
          this.#reconnectNow();
        }
      }
    });
  }

  get readyState() {
    return this.#socket?.readyState;
  }

  connect(): Promise<void> {
    if (this.#connectPromise) return this.#connectPromise;

    this.#connectPromise = (async () => {
      await waitRehydration();
      this.#ensureStoreSubscription();

      const accessToken = store.getState().auth.authInfo?.access_token;
      if (!accessToken) {
        this.#shouldReconnect = false;
        return;
      }

      this.#shouldReconnect = true;

      if (!this.#socket || this.readyState === WebSocket.CLOSED) {
        this.#createSocket();
        return;
      }

      if (this.readyState === WebSocket.CLOSING) {
        this.#socket.reconnect();
      }
    })().finally(() => {
      this.#connectPromise = null;
    });

    return this.#connectPromise;
  }

  #createSocket() {
    this.#socket?.close();
    this.#socket = new ReconnectingWebSocket(this.#url, [], {
      minReconnectionDelay: MIN_RECONNECTION_DELAY_MS,
      maxReconnectionDelay: MAX_RECONNECTION_DELAY_MS,
      reconnectionDelayGrowFactor: RECONNECTION_GROW_FACTOR,
      minUptime: MIN_UPTIME_MS,
      connectionTimeout: CONNECTION_TIMEOUT_MS,
      maxEnqueuedMessages: 0,
    });
    this.#wsAuthorized = false;

    this.#socket.onopen = (event) => {
      this.#startHeartbeat();
      this.#authorize();
      this.#eventBus.emit(InternalEvents.OPEN, event);
    };

    this.#socket.onclose = (event) => {
      this.#stopHeartbeat();
      this.#wsAuthorized = false;
      this.#eventBus.emit(InternalEvents.CLOSE, event);
    };

    this.#socket.onmessage = (event) => {
      this.#handleMessage(event.data);
    };
  }

  #handleMessage(data: string) {
    if (typeof data === 'string' && data.startsWith('{')) {
      try {
        const parsed = JSON.parse(data);
        if (parsed?.type === 'callback') {
          this.#eventBus.emit(InternalEvents.CALLBACK, parsed);
        } else if (parsed?.type) {
          this.#eventBus.emit(parsed.type, parsed.payload);
        }
        return;
      } catch {}
    }

    switch (data) {
      case EVENT_MESSAGES.AUTHORIZED:
        this.#wsAuthorized = true;
        this.#hasFailedAuth = false;
        this.#failedAuthToken = undefined;
        this.#eventBus.emit(EVENT_MESSAGES.AUTHORIZED);
        this.#flushMessageQueue();
        break;
      case EVENT_MESSAGES.FAILED_AUTHORIZATION:
        this.#wsAuthorized = false;
        this.#hasFailedAuth = true;
        this.#failedAuthToken =
          this.#lastAuthorizedToken ?? store.getState().auth.authInfo?.access_token;
        this.#eventBus.emit(EVENT_MESSAGES.FAILED_AUTHORIZATION);
        this.#noteFailedAuthAndMaybeDrop();
        void this.#refreshAndReauthorize();
        break;
      case EVENT_MESSAGES.CONNECTED:
        this.#eventBus.emit(EVENT_MESSAGES.CONNECTED);
        break;
      case EVENT_MESSAGES.PING:
        this.#eventBus.emit(EVENT_MESSAGES.PING);
        break;
      case EVENT_MESSAGES.PONG:
        this.#clearPongTimeout();
        this.#eventBus.emit(EVENT_MESSAGES.PONG);
        break;
      default:
        this.#eventBus.emit('unknown_raw', data);
    }
  }

  #startHeartbeat() {
    this.#stopHeartbeat();
    this.#heartbeatIntervalId = window.setInterval(() => {
      if (this.#socket?.readyState !== WebSocket.OPEN) return;
      try {
        this.#socket.send(EVENT_MESSAGES.PING);
        this.#clearPongTimeout();
        this.#pongTimeoutId = window.setTimeout(() => {
          this.#pongTimeoutId = undefined;
          // Stop immediately — don't wait for onclose, which may be delayed
          // on a zombie TCP connection
          this.#stopHeartbeat();
          this.#reconnectNow();
        }, PONG_TIMEOUT_MS);
      } catch {}
    }, HEARTBEAT_INTERVAL_MS);
  }

  #stopHeartbeat() {
    if (this.#heartbeatIntervalId !== undefined) {
      window.clearInterval(this.#heartbeatIntervalId);
      this.#heartbeatIntervalId = undefined;
    }
    this.#clearPongTimeout();
  }

  #clearPongTimeout() {
    if (this.#pongTimeoutId !== undefined) {
      window.clearTimeout(this.#pongTimeoutId);
      this.#pongTimeoutId = undefined;
    }
  }

  #reconnectNow() {
    if (!this.#shouldReconnect) return;

    const socket = this.#socket;
    if (socket && socket.readyState !== WebSocket.CLOSED) {
      try {
        socket.reconnect();
      } catch {}
    } else {
      void this.connect();
    }
  }

  #noteFailedAuthAndMaybeDrop() {
    const now = Date.now();
    this.#failedAuthTimestamps.push(now);
    this.#failedAuthTimestamps = this.#failedAuthTimestamps.filter(
      (t) => now - t <= this.#FAILED_AUTH_WINDOW_MS,
    );
    if (this.#failedAuthTimestamps.length >= this.#FAILED_AUTH_MAX) {
      this.#reconnectNow();
      this.#failedAuthTimestamps = [];
    }
  }

  async #refreshAndReauthorize() {
    if (!this.#authRefreshPromise) {
      this.#authRefreshPromise = (async () => {
        const success = await refreshToken();
        if (!success) {
          this.#reconnectNow();
        }
        return success;
      })().finally(() => {
        this.#authRefreshPromise = null;
      });
    }
    return this.#authRefreshPromise;
  }

  #flushMessageQueue() {
    if (!this.#wsAuthorized || !this.#messageQueue.length) return;
    const q = this.#messageQueue;
    this.#messageQueue = [];
    for (const message of q) {
      this.sendMessage(message);
    }
  }

  onOpen(callback: (event: Event) => void) {
    this.#eventBus.on(InternalEvents.OPEN, callback);
    return () => this.#eventBus.off(InternalEvents.OPEN, callback);
  }

  onClose(callback: (event: CloseEvent) => void) {
    this.#eventBus.on(InternalEvents.CLOSE, callback);
    return () => this.#eventBus.off(InternalEvents.CLOSE, callback);
  }

  onMessage<E extends EventName>(name: E, callback: (payload: EventPayloadByName[E]) => void) {
    this.#eventBus.on(name, callback);
    return () => this.#eventBus.off(name, callback);
  }

  emitLocalEvent<E extends EventName>(name: E, payload: EventPayloadByName[E]) {
    if (!isLocalDevRuntime()) return false;

    this.#eventBus.emit(name, payload);
    return true;
  }

  sendMessage(data: string) {
    if (this.readyState === WebSocket.OPEN && this.#wsAuthorized) {
      try {
        this.#socket?.send(data);
      } catch {
        this.#messageQueue.push(data);
      }
    } else {
      this.#messageQueue.push(data);
    }
  }

  sendJsonMessage(data: Record<string, unknown>) {
    this.sendMessage(JSON.stringify(data));
  }

  sendCallbackMessage<T extends Record<string, any>>(args: {
    method: string;
    params: Record<string, unknown>;
  }) {
    const id = nanoid(8);
    this.sendJsonMessage({ id, type: 'call', method: args.method, params: args.params });

    return new Promise<T>((resolve, reject) => {
      const onCallback = (data: T & { id?: string }) => {
        if (data?.id === id) {
          window.clearTimeout(timeoutId);
          this.#eventBus.off(InternalEvents.CALLBACK, onCallback);
          resolve(data);
        }
      };

      const timeoutId = window.setTimeout(() => {
        this.#eventBus.off(InternalEvents.CALLBACK, onCallback);
        reject(new Error('Callback timeout'));
      }, 30_000);

      this.#eventBus.on(InternalEvents.CALLBACK, onCallback);
    });
  }

  disconnect() {
    this.#shouldReconnect = false;
    this.#stopHeartbeat();
    this.#messageQueue = [];
    this.#wsAuthorized = false;
    this.#hasFailedAuth = false;
    this.#failedAuthToken = undefined;
    this.#connectPromise = null;

    try {
      this.#socket?.close();
    } catch {}
    this.#socket = null;

    this.#unsubscribeStore?.();
    this.#unsubscribeStore = undefined;
  }

  #authorize = () => {
    const accessToken = store.getState().auth.authInfo?.access_token;
    if (accessToken && this.#socket?.readyState === WebSocket.OPEN) {
      this.#lastAuthorizedToken = accessToken;
      try {
        this.#socket.send(accessToken);
      } catch {}
    }
  };
}
